import { MouseEventHandler, ReactNode } from "react";
import { cn } from "../../../utils/classes";
import styles from "./identity-control.module.css";
import { RailAvatar } from "./rail-avatar";

export type IdentityControlProps = {
  /** Display name — also used to derive fallback initials. */
  name: string;
  /** Secondary line shown under the name when expanded (email, role, …). */
  subtitle?: string;
  avatarUrl?: string | null;
  /** Mirrors the owning `<NavRail>`'s expanded state. */
  expanded?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Trailing affordance shown when expanded (e.g. a chevron/menu icon). */
  action?: ReactNode;
  className?: string;
};

/**
 * Bottom-left identity control (`design.md`'s "square avatar" convention —
 * 40x40, 6px radius, 1px border — via `<RailAvatar>`, a CSS-Modules-only
 * avatar built for this Tailwind-free component tree).
 * Avatar-only when the rail is collapsed; avatar + name (+ optional
 * subtitle/action) when expanded.
 */
export function IdentityControl({
  name,
  subtitle,
  avatarUrl,
  expanded = false,
  onClick,
  action,
  className,
}: IdentityControlProps) {
  const content = (
    <>
      <RailAvatar name={name} imageUrl={avatarUrl} />
      {expanded && (
        <span className={styles.meta}>
          <span className={styles.name}>{name}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </span>
      )}
      {expanded && action && <span className={styles.action}>{action}</span>}
    </>
  );

  const controlClassName = cn(
    styles.control,
    expanded && styles.expanded,
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={controlClassName}
        onClick={onClick}
        aria-label={name}
      >
        {content}
      </button>
    );
  }

  return <div className={controlClassName}>{content}</div>;
}
